import {
  MessageSquareTextIcon,
  CalendarIcon,
  FilesIcon,
  MailIcon,
  HouseIcon,
  ShieldCheckIcon,
  BookOpen,
  Building2,
  FilePenLine,
  UsersIcon,
  Scale,
  Layers,
} from 'lucide-react';
import { TimelineIcon } from '@/components/ui/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import type { ComponentProps } from 'react';
import NavigationItems from './NavigationItems';
import type { NavigationProps, MenuItem } from './NavigationItems';
import { cn } from '@/lib/utils';
import { useSuperAdminCheck } from '@/hooks/useSuperAdminCheck';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useFeatures, FEATURE_FLAGS } from '@/hooks/useFeatures';
import useMatter from '@/hooks/useMatter';
import { approvedTenants } from '@/config/approved-tenants';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { getEnvConfig } from '@/lib/env-config';
import { getJurisdiction } from '@/jurisdictions';
import { bundleBaseForRegion } from '@/lib/bundleRoutes';
import { useState, useEffect } from 'react';

const HIDE_DEEP_ANALYSIS_BETA_TENANTS = new Set([
  'crimson',
  'crimsonus',
  'crimsonlegal',
  'crimsonlegalus',
  'webergal',
  'demo',
  'test',
  'crimsontest',
]);

interface NavigationItemsProps extends NavigationProps {
  slug: string | undefined;
  collapsed?: boolean;
}

const SectionDivider = ({ collapsed }: { collapsed: boolean }) => {
  if (collapsed) {
    return <div className="w-6 mx-auto my-2.5 border-t border-white/10" />;
  }
  return <div className="mx-2.5 my-3 border-t border-white/10" />;
};

const SectionHeader = ({
  collapsed,
  label,
  showBeta = false,
}: {
  collapsed: boolean;
  label: string;
  showBeta?: boolean;
}) => {
  if (collapsed) {
    return <SectionDivider collapsed={collapsed} />;
  }

  return (
    <div className="mx-2.5 mt-3 mb-1.5">
      <div className="mb-2 border-t border-white/10" />
      <div className="flex items-center justify-between px-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-200">
          {label}
        </span>
        {showBeta && (
          <span className="shrink-0 rounded-sm bg-white/10 px-1 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-zinc-100">
            BETA
          </span>
        )}
      </div>
    </div>
  );
};

const TeamNavigation = ({ slug, activePathname, collapsed = false }: NavigationItemsProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSuperAdmin } = useSuperAdminCheck();
  const { isAdmin } = useAdminCheck();
  const { isEnabled: knowledgeGraphUiEnabled } = useFeatures(FEATURE_FLAGS.KNOWLEDGE_GRAPH_UI);
  const { isEnabled: draftingEnabled } = useFeatures(FEATURE_FLAGS.DRAFTING);
  const { matter } = useMatter(slug);
  const tenantName = approvedTenants[getEnvConfig().environment]?.[user?.tenantId ?? ''];
  const showDeepAnalysisBeta = !HIDE_DEEP_ANALYSIS_BETA_TENANTS.has(tenantName ?? '');
  const showKnowledgeGraphTabs = knowledgeGraphUiEnabled && matter?.knowledge_graph_enabled === true;
  const isUsMatter = matter?.region?.toUpperCase() === 'US';
  const bundleBase = bundleBaseForRegion(matter?.region);
  const bundleLabel = getJurisdiction(matter?.region || 'UK', matter?.forum ?? null).ui_labels.bundle_plural;

  // Initialize showAdminTab from localStorage synchronously to avoid flicker
  const [showAdminTab, setShowAdminTab] = useState(() => {
    const stored = localStorage.getItem('showAdminTab');
    return stored !== null ? stored === 'true' : true;
  });

  // Listen for changes to the Platform nav visibility setting
  useEffect(() => {
    const handleVisibilityChange = (event: CustomEvent) => {
      setShowAdminTab(event.detail.visible);
    };

    window.addEventListener('adminTabVisibilityChanged', handleVisibilityChange as EventListener);

    return () => {
      window.removeEventListener('adminTabVisibilityChanged', handleVisibilityChange as EventListener);
    };
  }, []);

  const mainMenus: MenuItem[] = [
    {
      name: t('home'),
      href: '/home',
      icon: HouseIcon,
      active: activePathname === '/home',
    },
  ];

  // Firm Admin hub — every organisation Admin
  if (isAdmin) {
    mainMenus.push({
      name: t('admin'),
      href: '/admin',
      icon: ShieldCheckIcon,
      // Exact match so /admin/tenants (Platform) does not highlight Admin
      active: activePathname === '/admin',
    });
  }

  // Platform — Crimson staff console (toggleable from Account settings)
  if (isSuperAdmin && showAdminTab) {
    mainMenus.push({
      name: t('platform'),
      href: '/admin/tenants',
      icon: Building2,
      active: activePathname?.startsWith('/admin/tenants') ?? false,
    });
  }

  const teamMenus: MenuItem[] = [
    {
      name: 'Case Overview',
      href: `/matter/${slug}/overview`,
      icon: (props: ComponentProps<typeof BookOpen>) => <BookOpen {...props} />,
      active: activePathname === `/matter/${slug}/overview`,
    },
    {
      name: t('correspondence'),
      href: `/matter/${slug}/case-files/correspondence`,
      icon: MailIcon,
      active: activePathname?.startsWith(
        `/matter/${slug}/case-files/correspondence`,
      ),
    },
    {
      name: t('case-files'),
      href: `/matter/${slug}/case-files`,
      icon: FilesIcon,
      active:
        activePathname?.startsWith(`/matter/${slug}/case-files`) &&
        !activePathname?.startsWith(
          `/matter/${slug}/case-files/correspondence`,
        ),
    },
    {
      name: bundleLabel,
      href: `/matter/${slug}/${bundleBase}`,
      icon: Layers,
      active:
        activePathname?.startsWith(`/matter/${slug}/bundles`) ||
        activePathname?.startsWith(`/matter/${slug}/binders`),
    },
    {
      name: t('deadlines'),
      href: `/matter/${slug}/deadlines`,
      icon: CalendarIcon,
      active: activePathname === `/matter/${slug}/deadlines`,
    },
    ...(isUsMatter ? [
      {
        name: 'Dockets',
        label: 'BETA',
        href: `/matter/${slug}/dockets`,
        icon: Scale,
        active: activePathname?.startsWith(`/matter/${slug}/dockets`),
      },
    ] : []),
    ...(draftingEnabled ? [
      {
        name: 'Drafting',
        href: `/matter/${slug}/drafting`,
        icon: FilePenLine,
        active: activePathname?.startsWith(`/matter/${slug}/drafting`)
          || activePathname?.startsWith(`/matter/${slug}/drafts`),
      },
    ] : []),
    {
      name: t('case-partner'),
      href: `/matter/${slug}/case-partner`,
      icon: MessageSquareTextIcon,
      active: activePathname === `/matter/${slug}/case-partner`,
      onClick: (event) => {
        if (!slug) return;

        event.preventDefault();
        navigate({
          to: '/matter/$slug/case-partner',
          params: { slug },
          search: { draft: Date.now().toString() },
        });
      },
    },
  ];

  const deepAnalysisMenus: MenuItem[] = showKnowledgeGraphTabs ? [
    {
      name: 'Entities',
      href: `/matter/${slug}/entities`,
      icon: UsersIcon,
      active: activePathname === `/matter/${slug}/entities`,
    },
    {
      name: 'Chronology',
      href: `/matter/${slug}/chronology`,
      icon: TimelineIcon,
      active: activePathname === `/matter/${slug}/chronology`,
    },
  ] : [];

  return (
    <div className={cn(collapsed ? "w-full" : "", "pt-1")}>
      <NavigationItems menus={mainMenus} collapsed={collapsed} />
      <SectionDivider collapsed={collapsed} />
      <NavigationItems menus={teamMenus} collapsed={collapsed} />
      {deepAnalysisMenus.length > 0 && (
        <>
          <SectionHeader collapsed={collapsed} label="Deep Analysis" showBeta={showDeepAnalysisBeta} />
          <NavigationItems menus={deepAnalysisMenus} collapsed={collapsed} />
        </>
      )}
    </div>
  );
};

export default TeamNavigation;
